import { useState, useEffect, useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import ClientsTable from './ClientsTable';
import AddClientModal from './AddClientModal';
import EditClientModal from './EditClientModal';
import ViewClientModal from './ViewClientModal';
import DeleteClientModal from './DeleteClientModal';
import { getClients, submitClient, deleteClient } from './../../services/ClientService';
import CampaignService from './../../services/CampaignService';
import PageHeader from '../../components/PageHeader/PageHeader';
import { useTranslation } from 'react-i18next';
import { hasStoredPermission } from '../../utils/dashboardPermissions';
import BulkImportButton from '../../components/BulkImport/BulkImportButton';

const Clients = () => {
  const { t } = useTranslation();

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const [loyaltyCardEnabled, setLoyaltyCardEnabled] = useState(false);
  const searchTimeoutRef = useRef(null);

  const fetchClients = useCallback(async (page, pageSize, search, currentSortModel = sortModel, currentFilterModel = filterModel) => {
    try {
      setLoading(true);
      const cleanSearch = search || '';
      const activeSort = Array.isArray(currentSortModel) ? currentSortModel[0] : null;
      const activeFilter = currentFilterModel?.items?.find((item) => item?.field && item?.operator);
      const response = await getClients(page, pageSize, cleanSearch, {
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort,
        filterField: activeFilter?.field,
        filterOperator: activeFilter?.operator,
        filterValue: activeFilter?.value,
      });
      
      if (response && typeof response === 'object') {
        const fetchedClients = response.clients || response.data || [];
        setClients(fetchedClients);
        setTotalRows(response.total || response.totalCount || 0);
      } else {
        const fetchedClients = response || [];
        setClients(fetchedClients);
        setTotalRows(fetchedClients?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [sortModel, filterModel]);

  // Cargar datos iniciales solo una vez
  useEffect(() => {
    fetchClients(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchClients, paginationModel.page, paginationModel.pageSize, sortModel, filterModel]);

  useEffect(() => {
    let mounted = true;

    const fetchLoyaltyCardCampaign = async () => {
      if (!hasStoredPermission('MANAGE_MARKETING')) {
        if (mounted) {
          setLoyaltyCardEnabled(false);
        }
        return;
      }

      try {
        const response = await CampaignService.getLoyaltyCardCampaign();
        const campaign = response?.data ?? response ?? null;
        if (mounted) {
          setLoyaltyCardEnabled(Boolean(campaign?.enabled));
        }
      } catch (error) {
        if (mounted) {
          setLoyaltyCardEnabled(false);
        }
      }
    };

    fetchLoyaltyCardCampaign();

    return () => {
      mounted = false;
    };
  }, []);

  // Funciones para manejar cambios de paginación
  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (newFilterModel) => {
    const normalizedItems = (newFilterModel?.items || [])
      .filter((item) => item?.field && item?.operator)
      .slice(0, 1);

    setFilterModel({ ...newFilterModel, items: normalizedItems });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Función para manejar cambios de búsqueda con debounce
  const handleSearchChange = useCallback((newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    const currentPageSize = paginationModel.pageSize;
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    searchTimeoutRef.current = setTimeout(() => {
      fetchClients(1, currentPageSize, newSearchTerm, sortModel, filterModel);
    }, 500);
  }, [paginationModel.pageSize, sortModel, filterModel, fetchClients]);

  const handleAddClient = async (newClient) => {
    try {
      await submitClient(newClient);
      fetchClients(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    } catch (error) {
      console.error('Error creating client:', error);
    }
  };
  
  const handleEditClient = async (updatedClient) => {
    try {
      await submitClient(updatedClient);
      fetchClients(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    } catch (error) {
      console.error('Error updating client:', error);
    }
  };  
  
  const handleDeleteClient = async () => {
    try {
      await deleteClient(selectedClient.ci);
      setIsDeleteModalOpen(false);
      fetchClients(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <PageHeader title={t('clients')} subtitle={t('clients_scope_subtitle')} />
        <BulkImportButton
          type="clients"
          label={t('clients')}
          onSuccess={() => fetchClients(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel)}
        />
      </Box>
      <ClientsTable
        loading={loading}
        clients={clients}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        totalRows={totalRows}
        loyaltyCardEnabled={loyaltyCardEnabled}
        onRefresh={() => fetchClients(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel)}
        onAdd={() => setIsAddModalOpen(true)}
        onEdit={(client) => {
          setSelectedClient(client);
          setIsEditModalOpen(true);
        }}
        onView={(client) => {
          setSelectedClient(client);
          setIsViewModalOpen(true);
        }}
        onDelete={(client) => {
          setSelectedClient(client);
          setIsDeleteModalOpen(true);
        }}
      />
      <AddClientModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddClient}
      />
      <EditClientModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditClient}
        client={selectedClient}
      />
      <ViewClientModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        client={selectedClient}
        loyaltyCardEnabled={loyaltyCardEnabled}
      />
      <DeleteClientModal
        clientName={selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : ''}
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteClient}
      />
    </Box>
  );
};

export default Clients;
